import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import { CButton, CImage } from '@coreui/react-pro'
import AppCard from '../../components/AppCard'
import AppTable from 'src/components/AppTable'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'

// icons
import AddMoreIconWhite from '../../assets/icons/AddMoreIconWhite.png'

// styling
import { styleButtonVars2 } from '../../components/custom-styles/buttonStyles'

// helpers
import Axios from '../../helpers/axios'
import { columnEmployeeList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from '../../helpers/format-queries'
import { capitalizeFirstLetter } from 'src/helpers/text-format'
import { singleStatusContractOptionSelect, singleMonthOptionSelect } from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const EmployeeList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })

  const [employeeList, setEmployeeList] = useState([]);
  const [employeeListCount, setEmployeeListCount] = useState(0);
  const [employeeListPerPage, setEmployeeListPerPage] = useState(10);
  const [activeEmployeeListPage, setActiveEmployeeListPage] = useState(1);
  const [searchEmployeeListFilter, setSearchEmployeeListFilter] = useState("");
  const [columnEmployeeListSorter, setColumnEmployeeListSorter] = useState({ column: "updatedAt", state: "desc" });

  const [isModalExportOpen, setIsModalExportOpen] = useState({ show: false });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedEmployeeType, setSelectedEmployeeType] = useState(null);

  useEffect(() => {
    let queries = {
      search: searchEmployeeListFilter,
      limit: employeeListPerPage,
      offset: employeeListPerPage * activeEmployeeListPage - employeeListPerPage,
      sort:
        (columnEmployeeListSorter &&
          `${columnEmployeeListSorter.column}%${columnEmployeeListSorter.state}`) ||
        "",
    };
    queries = formUrlQuery(queries);
    fetchAllEmployees(queries);
  }, [
    searchEmployeeListFilter,
    employeeListPerPage,
    activeEmployeeListPage,
    columnEmployeeListSorter,
  ]);

  const handleSearchItems = (search) => {
    setActiveEmployeeListPage(1)
    setSearchEmployeeListFilter(search);
  }

  const fetchAllEmployees = (queries) => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/employee?${queries}`
    })
      .then(({ data }) => {
        const tempEmployeeList = data?.value?.map((d) => ({
          ...d,
          name: d?.last_name ? `${d?.first_name} ${d?.last_name}` : d?.first_name,
          status: d?.status ? capitalizeFirstLetter(d.status) : '-',
          contact_number: `${d?.country?.phone_code}${d?.contact_number}`,
          is_active: d?.is_active ? 'Active' : 'Inactive'
        }))
        setEmployeeList(tempEmployeeList);
        if (activeEmployeeListPage === 1) {
          setEmployeeListCount(data?.count);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleDeleteConfirmation = () => {
    Axios({
      method: "delete",
      url: `/api/tracker/v1/employee/delete/${showConfirmDelete?.item?.id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmDelete({ show: false, item: {} })
          fetchAllEmployees()
          showSuccess('Employee has been successfully deleted')
          window.location.reload()
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnRowClick = (item, columnName) => {
    if (columnName !== 'action') {
      navigate(`/employee/details/${item.id}`)
    }
  }

  const formikExport = useFormik({
    initialValues: {
      month: '',
      employee_status: '',
    },
    validationSchema: Yup.object().shape({
      month: Yup.string().required("Please select month."),
      employee_status: Yup.string().required("Please select employee type."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleExport(values)
      setSubmitting(false)
    },
  });

  const handleSelectEmployeeType = (data) => {
    formikExport.setValues((values) => ({
      ...values,
      employee_status: data ? data?.value : '',
    }))

    setSelectedEmployeeType(data ? data : null)
  }

  const handleSelectMonth = (data) => {
    formikExport.setValues((values) => ({
      ...values,
      month: data ? data?.value : '',
    }))

    setSelectedMonth(data ? data : null)
  }

  const handleExport = async () => {
    const payload = {
      month: formikExport.values.month,
      employee_type: formikExport.values.employee_status,
    };

    dispatch({ type: 'SET_IS_LOADING', isLoading: true });

    try {
      const { data } = await Axios({
        method: "post",
        url: `/api/tracker/v1/employee/export/salary`,
        data: payload,
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });

      if (data) {
        const filename = `salary-report-${formikExport.values.employee_status}-${moment()
          .month(parseInt(formikExport.values.month) - 1)
          .format('MMMM-YYYY')}.xlsx`;

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('Success export salary');
        setIsModalExportOpen({ show: false });
        formikExport.resetForm();
        setSelectedMonth(null);
        setSelectedEmployeeType(null);
      }

    } catch (err) {
      console.error(err);
      let errMsg = 'Terjadi kesalahan saat export data';

      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errMsg = json.message || json.error || errMsg;
          console.error('Server error:', json);
        } catch (parseError) {
          console.error('Gagal parsing error blob:', parseError);
        }
      } else if (err?.response?.data?.message) {
        errMsg = err.response.data.message;
      }

      showError(errMsg);
    } finally {
      dispatch({ type: 'SET_IS_LOADING', isLoading: false });
    }
  };
  
  return (
    <>
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`Employee ${showConfirmDelete?.item?.first_name}`}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      {
        isModalExportOpen?.show && (
          <AppModalConfirmation
            confirmationType='export-salary'
            showModal={isModalExportOpen}
            setShowModal={setIsModalExportOpen}
            setSelectedMonth={handleSelectMonth}
            setSelectedEmployeeType={handleSelectEmployeeType}
            selectedMonth={selectedMonth}
            selectedEmployeeType={selectedEmployeeType}
            monthOptions={singleMonthOptionSelect()}
            statusContractOptions={singleStatusContractOptionSelect()}
            errors={formikExport.errors}
            handleSubmit={formikExport.handleSubmit}
            isLoading={isLoading}
          />
        )
      }
      <div className='d-grid gap-2 d-md-flex justify-content-md-end mb-3'>
        <CButton style={styleButtonVars2} onClick={() => setIsModalExportOpen({ show: true })}>
          Export Salary
        </CButton>
        <CButton style={styleButtonVars2} onClick={() => navigate('/employee/add')}>
          <CImage fluid src={AddMoreIconWhite} className='me-2' />
          Add Employee
        </CButton>
      </div>
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Employee List</h4>}
        bodyContent={
          <AppTable
            employeeListPage
            tableFilter
            setShowConfirmDelete={setShowConfirmDelete}
            tableColumns={columnEmployeeList}
            tableContent={employeeList}
            contentPerPage={employeeListPerPage}
            activeContentPage={activeEmployeeListPage}
            contentCount={employeeListCount}
            searchContentFilter={searchEmployeeListFilter}
            setActiveContentPage={setActiveEmployeeListPage}
            setSearchContentFilter={handleSearchItems}
            setContentPerPage={setEmployeeListPerPage}
            setColumnContentSorter={setColumnEmployeeListSorter}
            handleOnRowClick={handleOnRowClick}
            isClickable={true}
          />
        }
      />
    </>
  )
}

export default EmployeeList